// SingleInput.tsx
//
// A single-character input field for OTP or PIN entry, with keyboard navigation and accessibility support.
//
// This component is optimized for use in OTP/PIN forms, supporting auto-focus, keyboard navigation, and paste handling.

import React, {
  useEffect,
  useRef,
  KeyboardEvent,
  ChangeEvent,
  ClipboardEvent,
  RefObject,
} from "react";

/**
 * Props for the SingleInput component.
 */
export interface SingleInputProps {
  /** Unique id for the input (e.g., 'input1') */
  id: string;
  /** Id of the previous input (for left-arrow/backspace navigation) */
  previousId?: string | null;
  /** Id of the next input (for right-arrow/number navigation) */
  nextId?: string | null;
  /** Current value of the input (should be a single digit) */
  value: string;
  /** Error state for styling */
  error?: boolean | string | null;
  /** Callback when value changes */
  onValueChange: (idOrName: string, value: string) => void;
  /** Optional paste handler for OTP paste */
  handlePaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  /** If true, use 'name' prop for onValueChange (for settings forms) */
  isSettings?: boolean;
  /** Name attribute for the input (used if isSettings is true) */
  name?: string;
  /** Optional ref to control focus from parent */
  inputRef?: RefObject<HTMLInputElement>;
}

/**
 * SingleInput is a controlled input for a single OTP/PIN digit.
 * Handles keyboard navigation and value validation.
 */
const SingleInput: React.FC<SingleInputProps> = ({
  id,
  previousId,
  nextId,
  value,
  error,
  onValueChange,
  handlePaste,
  isSettings = false,
  name,
  inputRef,
}) => {
  // Fallback ref if none provided
  const defaultRef = useRef<HTMLInputElement>(null);
  const refToUse = inputRef || defaultRef;

  // Keyboard navigation: left/backspace to previous, right/number to next
  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" || e.key === "ArrowLeft") {
      if (previousId) {
        const prev = document.getElementById(
          previousId
        ) as HTMLInputElement | null;
        prev?.select();
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key === "ArrowRight") {
      if (nextId) {
        const next = document.getElementById(nextId) as HTMLInputElement | null;
        next?.select();
      }
    }
  };

  // Only allow a single digit (0-9)
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const singleDigit = /^\d$/.test(inputValue) ? inputValue : "";
    if (isSettings && name) onValueChange(name, singleDigit);
    else onValueChange(id, singleDigit);
  };

  // Auto-focus the first input on mount
  useEffect(() => {
    if (id === "input1") {
      refToUse.current?.focus();
    }
  }, [id, refToUse]);

  return (
    <div>
      <input
        ref={refToUse}
        onPaste={handlePaste}
        name={isSettings ? name : id}
        id={id}
        value={value}
        maxLength={1}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        className={`w-10 h-12 md:w-[45px] md:h-[60px] rounded-[10px] text-heading font-semibold text-18 md:text-20 placeholder:font-semibold placeholder:text-sm leading-[16px] bg-white text-center focus:outline-2 outline-[#C11E69] ${error ? "border border-red-500 shake" : ""}`}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="OTP Digit"
        aria-invalid={!!error}
      />
    </div>
  );
};

export default SingleInput;
